import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { onAuthStateChanged } from "firebase/auth";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  fetchManagedVerification,
  fetchPublicVerification,
  OrderVerificationData,
  updateVerificationStatus,
} from "./orderVerification.api";

const formatMoney = (value: number, currency: string) =>
  new Intl.NumberFormat("en-MW", {
    style: "currency",
    currency: currency || "MWK",
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatDateTime = (value?: string) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleString();
};

const getErrorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.message ||
  "Unable to load the verification.";

const OrderVerificationPage: React.FC = () => {
  const { token = "" } = useParams<{
    token: string;
  }>();

  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);

  const [updating, setUpdating] = useState(false);

  const [data, setData] = useState<OrderVerificationData | null>(null);

  const [error, setError] = useState("");

  const [selectedStatus, setSelectedStatus] = useState("");

  const [note, setNote] = useState("");

  const loadVerification = useCallback(async () => {
    if (!token) {
      setError("The verification token is missing.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const publicData = await fetchPublicVerification(token);

      setData(publicData);

      try {
        const managedData = await fetchManagedVerification(token);

        if (managedData) {
          setData(managedData);
        }
      } catch (manageError: any) {
        const status = manageError?.response?.status;

        if (status !== 401 && status !== 403) {
          console.error("Managed verification error:", manageError);
        }
      }
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadVerification();
  }, [loadVerification]);

  useEffect(() => {
    const auth = getAuth();

    return onAuthStateChanged(auth, () => {
      loadVerification();
    });
  }, [loadVerification]);

  useEffect(() => {
    const nextStatuses = data?.allowedNextStatuses || [];

    setSelectedStatus(nextStatuses[0] || "");
  }, [data?.allowedNextStatuses]);

  const isPaid = data?.payment.status === "paid";

  const orderTypeLabel = useMemo(
    () =>
      String(data?.orderType || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (value) => value.toUpperCase()),
    [data?.orderType],
  );

  const handleUpdate = async () => {
    if (!token || !selectedStatus) {
      return;
    }

    try {
      setUpdating(true);

      const updated = await updateVerificationStatus(
        token,
        selectedStatus,
        note,
      );

      setData(updated);
      setNote("");

      toast({
        title: "Status updated",
        status: "success",
        duration: 3500,
        isClosable: true,
      });
    } catch (updateError) {
      toast({
        title: "Update failed",
        description: getErrorMessage(updateError),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Flex minH="70vh" align="center" justify="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error || !data) {
    return (
      <Box maxW="720px" mx="auto" px={4} py={12}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertDescription>
            {error || "Verification not found."}
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      maxW="820px"
      mx="auto"
      px={{
        base: 4,
        md: 6,
      }}
      py={{
        base: 6,
        md: 10,
      }}
    >
      <Stack spacing={6}>
        <Box>
          <Badge colorScheme="green" px={3} py={1} borderRadius="full">
            Valid PickDengu ticket
          </Badge>

          <Heading mt={3} size="lg">
            Order verification
          </Heading>

          <Text mt={1} color="gray.600">
            Reference: {data.orderReference}
          </Text>
        </Box>

        <Box
          borderWidth="1px"
          borderRadius="xl"
          p={{
            base: 4,
            md: 6,
          }}
        >
          <Stack spacing={4}>
            <Flex
              justify="space-between"
              align={{
                base: "flex-start",
                md: "center",
              }}
              direction={{
                base: "column",
                md: "row",
              }}
              gap={2}
            >
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Payment
                </Text>
                <Heading size="md">{data.payment.label}</Heading>
              </Box>

              <Badge
                colorScheme={isPaid ? "green" : "orange"}
                fontSize="sm"
                px={3}
                py={1}
                borderRadius="full"
              >
                {isPaid ? "PAYMENT VERIFIED" : "NOT YET PAID"}
              </Badge>
            </Flex>

            <Divider />

            <Flex justify="space-between" gap={4} wrap="wrap">
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Amount
                </Text>
                <Text fontWeight="semibold">
                  {formatMoney(data.payment.amount, data.payment.currency)}
                </Text>
              </Box>

              <Box>
                <Text fontSize="sm" color="gray.500">
                  Order type
                </Text>
                <Text fontWeight="semibold">{orderTypeLabel}</Text>
              </Box>

              <Box>
                <Text fontSize="sm" color="gray.500">
                  Current status
                </Text>
                <Badge colorScheme="blue" mt={1}>
                  {data.fulfillment.label}
                </Badge>
              </Box>
            </Flex>
          </Stack>
        </Box>

        <Box
          borderWidth="1px"
          borderRadius="xl"
          p={{
            base: 4,
            md: 6,
          }}
        >
          <Heading size="md" mb={4}>
            Order details
          </Heading>

          <Stack spacing={2}>
            {data.summary.title && (
              <Text>
                <strong>Item/service:</strong> {data.summary.title}
              </Text>
            )}

            {data.summary.providerName && (
              <Text>
                <strong>Provider:</strong> {data.summary.providerName}
              </Text>
            )}

            {data.summary.scheduledAt && (
              <Text>
                <strong>Scheduled:</strong>{" "}
                {formatDateTime(data.summary.scheduledAt)}
              </Text>
            )}

            {data.summary.maskedCustomerName && (
              <Text>
                <strong>Customer:</strong> {data.summary.maskedCustomerName}
              </Text>
            )}

            {data.summary.vehicleName && (
              <Text>
                <strong>Vehicle:</strong> {data.summary.vehicleName}
              </Text>
            )}

            {data.summary.accommodationName && (
              <Text>
                <strong>Accommodation:</strong> {data.summary.accommodationName}
              </Text>
            )}

            {data.summary.pickupLocation && (
              <Text>
                <strong>Pickup:</strong> {data.summary.pickupLocation}
              </Text>
            )}

            {data.summary.destination && (
              <Text>
                <strong>Destination:</strong> {data.summary.destination}
              </Text>
            )}
          </Stack>
        </Box>

        {data.canManage ? (
          <Box
            borderWidth="1px"
            borderRadius="xl"
            p={{
              base: 4,
              md: 6,
            }}
          >
            <Heading size="md" mb={4}>
              Manage order
            </Heading>

            {!isPaid && (
              <Alert status="warning" mb={4} borderRadius="md">
                <AlertIcon />
                Do not provide the service until payment is confirmed.
              </Alert>
            )}

            {(data.allowedNextStatuses || []).length > 0 ? (
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel>Update status</FormLabel>
                  <Select
                    value={selectedStatus}
                    onChange={(event) => setSelectedStatus(event.target.value)}
                  >
                    {(data.allowedNextStatuses || []).map((status) => (
                      <option key={status} value={status}>
                        {status
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (value) => value.toUpperCase())}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Note (optional)</FormLabel>
                  <Textarea
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Add a short operational note"
                  />
                </FormControl>

                <Button
                  colorScheme="blue"
                  onClick={handleUpdate}
                  isLoading={updating}
                  isDisabled={!selectedStatus}
                >
                  Update status
                </Button>
              </Stack>
            ) : (
              <Text color="gray.600">
                No further status actions are available.
              </Text>
            )}
          </Box>
        ) : (
          <Box borderWidth="1px" borderRadius="xl" p={4}>
            <Text fontWeight="semibold">
              Are you the seller, driver, or service owner?
            </Text>
            <Text mt={1} color="gray.600">
              Sign in to manage this order when your account is authorized.
            </Text>
            <Button
              mt={4}
              variant="outline"
              onClick={() =>
                navigate(
                  `/login?returnUrl=${encodeURIComponent(location.pathname)}`,
                )
              }
            >
              Sign in
            </Button>
          </Box>
        )}

        {data.timeline.length > 0 && (
          <Box
            borderWidth="1px"
            borderRadius="xl"
            p={{
              base: 4,
              md: 6,
            }}
          >
            <Heading size="md" mb={4}>
              Status history
            </Heading>

            <Stack spacing={4}>
              {data.timeline.map((item, index) => (
                <Box key={`${item.status}-${item.changedAt}-${index}`}>
                  <Flex justify="space-between" gap={4} wrap="wrap">
                    <Text fontWeight="semibold">{item.label}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {formatDateTime(item.changedAt)}
                    </Text>
                  </Flex>

                  {item.note && (
                    <Text mt={1} color="gray.600">
                      {item.note}
                    </Text>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default OrderVerificationPage;
